import React, {createContext, useContext, useEffect, useState} from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {apiUrl} from './api-url';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Configure axios defaults
axios.defaults.baseURL = apiUrl;

// Add request interceptor to include token
axios.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    },
);

// Add response interceptor to handle token expiration
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            console.log('401 error, resetting auth');
            console.log(error);
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    },
);

export const AuthProvider = ({children}) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Initialize auth state
    useEffect(() => {
        const initAuth = async () => {
            try {
                const token = localStorage.getItem('token');
                const savedUser = localStorage.getItem('user');

                if (token && savedUser) {
                    const userData = JSON.parse(savedUser);
                    setUser(userData);
                    setIsAuthenticated(true);

                    // Verify token is still valid
                    try {
                        const response = await axios.get('/api/auth/profile');
                        setUser(response.data.user);
                        localStorage.setItem('user', JSON.stringify(response.data.user));
                    } catch (error) {
                        // Token is invalid, clear auth state
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                        setUser(null);
                        setIsAuthenticated(false);
                    }
                }
            } catch (error) {
                console.error('Auth initialization error:', error);
            } finally {
                setLoading(false);
            }
        };

        initAuth();
    }, []);

    const login = async (user, token) => {
        try {
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));

            setUser(user);
            setIsAuthenticated(true);

            toast.success(`Welcome back, ${user.firstName}!`);
            return {success: true, user: user};
        } catch (error) {
            const message = error.response?.data?.error || 'Login failed';
            toast.error(message);
            return {success: false, error: message};
        }
    };

    const register = async (userData) => {
        try {
            const response = await axios.post('/auth/register', userData);
            const {user: newUser, token} = response.data;

            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(newUser));

            setUser(newUser);
            setIsAuthenticated(true);

            toast.success(`Welcome to ArtSwap, ${newUser.firstName}!`);
            return {success: true, user: newUser};
        } catch (error) {
            const message = error.response?.data?.error || 'Registration failed';
            toast.error(message);
            return {success: false, error: message};
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setIsAuthenticated(false);
        toast.success('Logged out successfully');
    };

    const updateProfile = async (profileData) => {
        try {
            const response = await axios.put('/auth/profile', profileData);
            const updatedUser = response.data.user;

            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));

            toast.success('Profile updated successfully');
            return {success: true, user: updatedUser};
        } catch (error) {
            const message = error.response?.data?.error || 'Profile update failed';
            toast.error(message);
            return {success: false, error: message};
        }
    };

    const changePassword = async (currentPassword, newPassword) => {
        try {
            await axios.post('/auth/change-password', {
                currentPassword,
                newPassword,
            });

            toast.success('Password changed successfully');
            return {success: true};
        } catch (error) {
            const message = error.response?.data?.error || 'Password change failed';
            toast.error(message);
            return {success: false, error: message};
        }
    };

    const refreshUser = async () => {
        try {
            const response = await axios.get('/auth/profile');
            const userData = response.data.user;

            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));

            return {success: true, user: userData};
        } catch (error) {
            console.error('Failed to refresh user:', error);
            return {success: false, error: 'Failed to refresh user data'};
        }
    };

    const value = {
        user,
        isAuthenticated,
        loading,
        login,
        register,
        logout,
        updateProfile,
        changePassword,
        refreshUser,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
